'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { SPACING } from '@/constants/spacing';
import { TYPOGRAPHY } from '@/constants/typography';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { statics } from '@/data/store';
import { formatMakeName, deduplicateModels } from '@/utils/formatters';
import { postCmsLead } from '@/lib/cms';
import { FormRow, FormField as Field, Label, Input, Select, FieldError, SubmitButton, StatusMessage } from '@/components/ui/FormField';

// Trigger point: roughly the middle of the 30-40% scroll range the popup is
// meant to appear in. Measured against overall page scroll, not just the
// <Article> element - a simplification that's close enough given the post
// template's layout (a fixed-size hero sits above the article, so page-scroll
// % tracks article-scroll % closely for most of a normal read) and avoids
// needing to thread the article ref down into this component.
const TRIGGER_SCROLL_FRACTION = 0.35;
const DISMISS_STORAGE_KEY = 'gf_blog_lead_dismissed';

interface VehicleOption {
  id: string;
  name: string;
  title: string;
}
interface EngineOption {
  displacement: string;
  engine: string;
  intake: string;
  description: string;
}

function alreadyDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch {
    // sessionStorage can throw in some privacy modes - fail open (show once)
    // rather than crash the page.
    return false;
  }
}

function dedupeByDisplacement(engines: EngineOption[]): EngineOption[] {
  const seen = new Set<string>();
  const deduplicated: EngineOption[] = [];

  for (const eng of engines) {
    if (!seen.has(eng.displacement)) {
      seen.add(eng.displacement);
      deduplicated.push(eng);
    }
  }

  return deduplicated;
}

function markDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_STORAGE_KEY, '1');
  } catch {
    // Nothing we can do if storage is unavailable - the popup just won't be
    // suppressed across reloads in that session, which is a minor UX
    // inconvenience, not a functional break.
  }
}

export default function BlogLeadPopup({ postSlug }: { postSlug: string }) {
  const [visible, setVisible] = useState(false);
  const hasTriggered = useRef(false);

  useEffect(() => {
    if (alreadyDismissedThisSession()) return;

    let ticking = false;
    const checkScroll = () => {
      ticking = false;
      if (hasTriggered.current) return;

      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const fraction = docHeight > 0 ? scrollTop / docHeight : 0;

      if (fraction >= TRIGGER_SCROLL_FRACTION) {
        hasTriggered.current = true;
        setVisible(true);
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(checkScroll);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    markDismissed();
  }, []);

  const handleSubmitted = useCallback(() => {
    markDismissed();
    setVisible(false);
  }, []);

  // While open: lock the page behind the popup (otherwise mobile ends up
  // with two scrollable layers) and let Escape close it. The lock goes on
  // <html>, not <body>: the global styles give <html> overflow-x: clip, which
  // stops body's overflow from propagating to the viewport, so locking only
  // <body> leaves the page scrollable.
  useEffect(() => {
    if (!visible) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [visible, handleClose]);

  if (!visible) return null;

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton type="button" onClick={handleClose} aria-label="Close">
          &times;
        </CloseButton>
        <Heading>Need Help Finding Your Filter?</Heading>
        <Subheading>Tell us about your vehicle and we&apos;ll recommend the right Green Filter for it.</Subheading>
        <LeadForm postSlug={postSlug} onSubmitted={handleSubmitted} />
      </Modal>
    </Overlay>
  );
}

function LeadForm({ postSlug, onSubmitted }: { postSlug: string; onSubmitted: () => void }) {
  const [year, setYear] = useState('');
  const [makeId, setMakeId] = useState('');
  const [modelName, setModelName] = useState('');
  const [engine, setEngine] = useState('');

  const [makes, setMakes] = useState<VehicleOption[]>([]);
  const [models, setModels] = useState<VehicleOption[]>([]);
  const [engines, setEngines] = useState<EngineOption[]>([]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Cascading fetch: same /product-search endpoint and query-param shape
  // used by the main vehicle selector (components/PerfectFilter.tsx), just
  // trimmed to only what this form needs (the option lists, not product
  // results) since this popup is lead capture, not a product search.
  useEffect(() => {
    const params = new URLSearchParams();
    if (year) params.append('year', year);
    if (makeId) params.append('make_id', makeId);
    if (modelName) params.append('model_name', modelName);

    if (!year) return;

    const run = async () => {
      try {
        const res = await fetch(`${statics.BaseUrl}/product-search?${params.toString()}`);
        const json = await res.json();
        const rows = json?.data || [];
        if (!makeId) setMakes(rows);
        // The API returns one row per model+engine fitment, so the same
        // model (e.g. "Camry") can appear several times - dedupe the same
        // way the main vehicle selector does (components/PerfectFilter.tsx).
        else if (!modelName) setModels(deduplicateModels(rows));
        // Same duplication issue for engines, but keyed by displacement
        // (the field used as the option's key/value below) since engine
        // rows have no separate name/title pair.
        else if (!engine) setEngines(dedupeByDisplacement(rows));
      } catch (err) {
        console.error('BlogLeadPopup vehicle lookup failed:', err);
      }
    };

    run();
  }, [year, makeId, modelName, engine]);

  const yearOptions = (() => {
    const arr: number[] = [];
    const end = new Date().getFullYear() + 1; // include next model year
    for (let i = end; i >= 1959; i--) arr.push(i);
    return arr;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Enter a valid email address.');
      return;
    }

    setStatus('submitting');
    const selectedMake = makes.find((m) => m.id === makeId);
    const selectedEngine = engines.find((e) => e.displacement === engine);

    const result = await postCmsLead({
      name: name.trim() || undefined,
      email: email.trim(),
      phone: phone.trim() || undefined,
      vehicleYear: year || undefined,
      vehicleMake: selectedMake ? formatMakeName(selectedMake.title) : undefined,
      vehicleModel: modelName || undefined,
      vehicleEngine: selectedEngine ? selectedEngine.description || selectedEngine.engine : engine || undefined,
      postSlug,
    });

    if (result.success) {
      setStatus('success');
      setStatusMessage("Thanks! We'll follow up with a recommendation shortly.");
      setTimeout(onSubmitted, 1800);
    } else {
      setStatus('error');
      setStatusMessage(result.message || 'Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return <StatusMessage $variant="success">{statusMessage}</StatusMessage>;
  }

  return (
    <form onSubmit={handleSubmit}>
      {status === 'error' && <StatusMessage $variant="error">{statusMessage}</StatusMessage>}

      <FormRow>
        <Field>
          <Label htmlFor="lead-year">Year</Label>
          <Select id="lead-year" value={year} onChange={(e) => { setYear(e.target.value); setMakeId(''); setModelName(''); setEngine(''); setModels([]); setEngines([]); }}>
            <option value="">Select year</option>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label htmlFor="lead-make">Make</Label>
          <Select id="lead-make" value={makeId} disabled={!year} onChange={(e) => { setMakeId(e.target.value); setModelName(''); setEngine(''); setEngines([]); }}>
            <option value="">Select make</option>
            {makes.map((m) => (
              <option key={m.id} value={m.id}>{formatMakeName(m.title)}</option>
            ))}
          </Select>
        </Field>
      </FormRow>

      <FormRow>
        <Field>
          <Label htmlFor="lead-model">Model</Label>
          <Select id="lead-model" value={modelName} disabled={!makeId} onChange={(e) => { setModelName(e.target.value); setEngine(''); }}>
            <option value="">Select model</option>
            {models.map((m) => (
              <option key={m.id} value={m.title}>{m.title}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label htmlFor="lead-engine">Engine</Label>
          <Select id="lead-engine" value={engine} disabled={!modelName} onChange={(e) => setEngine(e.target.value)}>
            <option value="">Select engine</option>
            {engines.map((eng) => (
              <option key={eng.displacement} value={eng.displacement}>{eng.description || eng.engine}</option>
            ))}
          </Select>
        </Field>
      </FormRow>

      <Field>
        <Label htmlFor="lead-name">Name</Label>
        <Input id="lead-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
      </Field>

      <FormRow>
        <Field>
          <Label htmlFor="lead-email">Email *</Label>
          <Input id="lead-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" $hasError={!!emailError} />
          {emailError && <FieldError>{emailError}</FieldError>}
        </Field>
        <Field>
          <Label htmlFor="lead-phone">Phone</Label>
          <Input id="lead-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" />
        </Field>
      </FormRow>

      <SubmitButton type="submit" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending...' : 'Get My Recommendation'}
      </SubmitButton>
    </form>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above the fixed header (99996), shipping banner (99999) and mobile
     drawer (100001) - at 1000 they covered the close button on mobile. */
  z-index: 100002;
  overscroll-behavior: contain;
  padding: ${SPACING.md};
  animation: fadeIn 0.25s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  position: relative;
  background: ${COLORS.white};
  border-radius: 16px;
  padding: ${SPACING.xl};
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  /* dvh tracks the visible area on mobile browsers (vh includes the space
     behind the address bar, which cut off the bottom of the form). */
  max-height: 90dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: ${BREAKPOINTS.mobile}) {
    padding: ${SPACING.lg};
  }
`;

const CloseButton = styled.button`
  /* Sticky so it stays reachable while the form is scrolled on small screens. */
  position: sticky;
  top: 0;
  float: right;
  margin: -${SPACING.sm} -${SPACING.sm} 0 0;
  z-index: 1;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.white};
  border: none;
  border-radius: 50%;
  font-size: 28px;
  line-height: 1;
  color: ${COLORS.textMuted};
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${COLORS.textPrimary};
  }
`;

const Heading = styled.h2`
  ${TYPOGRAPHY.h3}
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.xs};
  padding-right: ${SPACING.xl};
`;

const Subheading = styled.p`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  margin: 0 0 ${SPACING.lg};
`;
